// CID Agent PWA API layer
// Module: Internal Co-Share Form — the fee-sharing agreement between the RENs on
// a multi-agent sale.
//
// The document is generated from the sale, not filled in: the property, the
// figures, the RENs and their shares all come off the record. What the agents add
// is their signatures. It is filed against the sale by reference (like an issued
// OTP), so it stays live and re-renders rather than becoming a stale PDF.
import client from './client';

/**
 * GET /api/agent/sales/:id/co-share-form
 * → { applicable, agent_count, form, history }
 *
 * `applicable` is false on a single-agent sale — there is nothing to share, so
 * the screen should not offer the form at all. `form.drift` says whether the
 * sale's split has moved since the form was signed.
 */
export const getSaleCoShareForm = (saleId) => client.get(`/api/agent/sales/${saleId}/co-share-form`);

/**
 * POST /api/agent/sales/:id/co-share-form
 * Draft the form from the sale's current split.
 *
 * When the letterhead branch is ambiguous — the RENs sit in different branches —
 * this returns 200 with `{ needs_branch: true, choices, preferred_branch_id }`
 * instead of creating anything. That is a question, not an error: re-post with
 * `{ branch_id }` once the agent has picked. The system deliberately does not
 * infer a branch, because a wrong letterhead on a signed fee agreement only
 * surfaces when someone disputes the split.
 */
export const createSaleCoShareForm = (saleId, data = {}) =>
  client.post(`/api/agent/sales/${saleId}/co-share-form`, data);

/** GET /api/agent/co-share-forms/:id — the record, with each REN's signing state. */
export const getCoShareFormById = (id) => client.get(`/api/agent/co-share-forms/${id}`);

/** GET /api/agent/co-share-forms/:id/preview → { html } — the document itself. */
export const getCoShareFormPreview = (id) => client.get(`/api/agent/co-share-forms/${id}/preview`);

/**
 * PATCH /api/agent/co-share-forms/:id/issue
 * Freezes the figures and the letterhead, prints the company witness, notifies
 * every other REN, and files it against the sale.
 */
export const issueCoShareForm = (id, data = {}) => client.patch(`/api/agent/co-share-forms/${id}/issue`, data);

/**
 * PATCH /api/agent/co-share-forms/:id/sign
 * Body: { signature_data_url }. A REN signs their OWN row — the server locates it
 * by the caller's agent id and ignores anything in the body that says otherwise.
 */
export const signCoShareForm = (id, data = {}) => client.patch(`/api/agent/co-share-forms/${id}/sign`, data);

/** PATCH /api/agent/co-share-forms/:id/void — before it is fully signed. Body: { reason }. */
export const voidCoShareForm = (id, data = {}) => client.patch(`/api/agent/co-share-forms/${id}/void`, data);

/**
 * POST /api/agent/co-share-forms/:id/reissue
 * Clause 5 of the document: a variation means new terms signed again. The old
 * version is superseded — never edited — and a fresh one is drafted from the
 * sale as it now stands, on the same letterhead.
 */
export const reissueCoShareForm = (id, data = {}) => client.post(`/api/agent/co-share-forms/${id}/reissue`, data);
